import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly iconOnly?: boolean;
    readonly interactive?: boolean;
    readonly onClick?: (e: MouseEvent) => void;
    readonly variant?: 'active-card' | 'error' | 'gray' | 'info' | 'orange' | 'success' | 'warning';
};

const baseClasses =
    'inline-flex flex-row items-center justify-center font-sans font-medium tabular-nums transition-colors duration-200 ease select-none whitespace-nowrap border';

const iconSizeClasses = '[&>svg]:h-3 [&>svg]:w-3 [&>svg]:shrink-0';
const iconOnlyClasses = 'h-[24px] w-[24px] gap-0 text-xxs';
const contentClasses =
    'h-[24px] min-w-[32px] px-m gap-xs text-xxs [&:has(>svg:first-child)]:pl-s [&:has(>svg:last-child)]:pr-s';

const variantBaseClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card': 'bg-orange-600 border-orange-500 text-orange-50',
    error: 'bg-error-25 border-error-200 text-error-700',
    gray: 'bg-gray-100 border-gray-300 text-gray-800',
    info: 'bg-info-25 border-info-100 text-info-300',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    success: 'bg-success-25 border-success-100 text-success-600',
    warning: 'bg-warning-50 border-warning-200 text-warning-800',
};

const variantHoverClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card': 'hover:bg-orange-700 hover:border-orange-400 hover:text-orange-50 hover:scale-[1.01]',
    error: 'hover:bg-error-100 hover:border-error-500 hover:text-error-800 hover:scale-[1.01]',
    gray: 'hover:bg-gray-200',
    info: 'hover:bg-info-50',
    orange: 'hover:bg-orange-50 hover:border-orange-500 hover:text-orange-900 hover:scale-[1.01]',
    success: 'hover:bg-success-50',
    warning: 'hover:bg-warning-100',
};

const variantActiveClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card': 'active:bg-orange-800 active:border-orange-300 active:text-orange-50 active:scale-[0.98]',
    error: 'active:bg-error-50 active:border-error-700 active:text-error-800 active:scale-[0.98]',
    gray: 'active:bg-gray-200 active:border-gray-800 active:text-gray-900 active:scale-[0.98]',
    info: 'active:bg-info-50 active:border-info-700 active:text-info-800 active:scale-[0.98]',
    orange: 'active:bg-orange-100 active:border-orange-700 active:text-orange-900 active:scale-[0.98]',
    success: 'active:bg-success-50 active:border-success-700 active:text-success-800 active:scale-[0.98]',
    warning: 'active:bg-warning-50 active:border-warning-700 active:text-warning-900 active:scale-[0.98]',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
    (
        {
            children,
            className = '',
            disabled = false,
            iconOnly = false,
            interactive = false,
            onClick,
            variant = 'gray',
            ...props
        },
        ref,
    ) => {
        const isClickable = interactive || !is.undefined(onClick);

        return (
            // biome-ignore lint/a11y/noStaticElementInteractions: suppressed due to migration
            // biome-ignore lint/a11y/useKeyWithClickEvents: suppressed due to migration
            <span
                ref={ref}
                className={clsx(
                    baseClasses,
                    iconOnly ? 'rounded-[50%]' : 'rounded-full',
                    iconOnly ? iconOnlyClasses : contentClasses,
                    iconSizeClasses,
                    variantBaseClasses[variant],
                    disabled && 'opacity-50',
                    isClickable && !disabled && variantHoverClasses[variant],
                    isClickable && !disabled && variantActiveClasses[variant],
                    isClickable && !disabled ? 'cursor-pointer' : 'cursor-default',
                    className,
                )}
                onClick={disabled ? undefined : onClick}
                {...props}
            >
                {children}
            </span>
        );
    },
);

Badge.displayName = 'Badge';
